import type { Person } from '@local/common-lib';
import { quickPatrolRequestContract } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Issue } from 'validata';
import { useFetchAllCache } from '../../hooks/use-cache.js';
import { usePromise } from '../../hooks/use-promise.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { formikCheck, issuesToFormik } from '../../utils/validation.js';
import type { QuickPatrolData } from './quick-patrol.view.js';
import { QuickPatrolView } from './quick-patrol.view.js';
import { useCallSigns } from './use-call-signs.js';

/**
 * Calculate the patrol start time based on whether immediate start is requested.
 * @param startIn10Min - If true, start in 10 minutes from now; otherwise use the provided time
 * @param scheduledTime - The scheduled start time to use if not starting in 10 minutes
 * @returns The calculated start time
 */
const calculatePatrolStartTime = (
  startIn10Min: boolean,
  scheduledTime: Date,
): Date => {
  return startIn10Min
    ? DateTime.now().plus({ minutes: 10 }).toJSDate()
    : scheduledTime;
};

export const QuickPatrolPage: FC = () => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { callSigns, restrictCallSigns } = useCallSigns();

  // Fetch persons for patroller selection
  const personCache = useFetchAllCache('person');
  const [allPersons, , state] = usePromise(
    () => personCache.all<Person.View>(),
    [personCache],
  );
  const persons = useMemo(() => {
    const filtered =
      allPersons?.filter(
        (person) =>
          !person.finished &&
          person.roles.includes('patroller') &&
          person._id !== auth.userInfo.userId, // Exclude current user
      ) ?? [];
    filtered.sort((a: Person.View, b: Person.View) =>
      a.lastName.localeCompare(b.lastName),
    );
    return filtered;
  }, [allPersons, auth.userInfo.userId]);

  const formik = useFormik<QuickPatrolData>({
    initialValues: {
      callSign: '',
      hours: 3,
      name: 'Patrol',
      patrollers: [],
      sendToEccLogOn: false,
      startIn10Min: true,
      when: DateTime.now().plus({ minutes: 30 }).toJSDate(),
    },
    onSubmit: async (values) => {
      const when = calculatePatrolStartTime(values.startIn10Min, values.when);

      const body = {
        callSign: values.callSign,
        hours: values.hours,
        name: values.name,
        patrollers: values.patrollers,
        sendToEccLogOn: values.startIn10Min || values.sendToEccLogOn,
        when,
      };

      try {
        const { data } = await auth.axios.post<{
          patrolId: string;
          sendToEccLogOn: boolean;
        }>('/api/v1/patrol/quick-patrol', body);

        enqueueSnackbar('Quick patrol created successfully', {
          variant: 'success',
        });

        // Navigate based on sendToEccLogOn flag
        if (data.sendToEccLogOn) {
          navigate(`/patrol/${data.patrolId}/ecc-log-on?immediate=true`);
        } else {
          navigate(`/patrol/${data.patrolId}`);
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);

        const axiosError = error as AxiosError<{ issues: Issue[] }>;
        if (axiosError.response && axiosError.response.status === 400) {
          if ('issues' in axiosError.response.data) {
            const issues = axiosError.response.data.issues;
            formik.setErrors(issuesToFormik(issues));
            return;
          }
        }

        enqueueSnackbar('Unable to create quick patrol', { variant: 'error' });
      }
    },
    validate: (value) => {
      const whenValue = calculatePatrolStartTime(
        value.startIn10Min,
        value.when,
      );

      return formikCheck(quickPatrolRequestContract, {
        callSign: value.callSign,
        hours: value.hours,
        name: value.name,
        patrollers: value.patrollers,
        sendToEccLogOn: value.startIn10Min || value.sendToEccLogOn,
        when: whenValue,
      });
    },
  });

  useSetTitle('Quick Patrol');

  const handleCancel = (): void => {
    navigate(-1);
  };

  return (
    <QuickPatrolView
      callSigns={callSigns}
      cancel={handleCancel}
      formik={formik}
      persons={persons}
      personsLoading={state === 'pending'}
      restrictCallSigns={restrictCallSigns}
    />
  );
};
